import axios from 'axios';
import jwtDecode from 'jwt-decode';
import * as genericUtils from '@tolk-ai/generic-utils';

const isRestError = maybeError => genericUtils.isObject(maybeError) && genericUtils.isString(maybeError.error);

const isAxiosRestError = maybeAxiosError => {
  var _a;

  return genericUtils.isObject(maybeAxiosError) && axios.isAxiosError(maybeAxiosError) && isRestError((_a = maybeAxiosError.response) === null || _a === void 0 ? void 0 : _a.data);
};

const isAxiosError = maybeAxiosError => genericUtils.isObject(maybeAxiosError) && axios.isAxiosError(maybeAxiosError);

const setCookie = (document, cookieDomain) => (name, value, secondsBeforeExpiration) => {
  const expirationDate = genericUtils.isNumber(secondsBeforeExpiration) ? new Date(Date.now() + secondsBeforeExpiration * genericUtils.TIME.MILLISECONDS_IN_A_SECOND) : new Date(Date.now() + genericUtils.TIME.SECONDS_IN_A_DECADE * genericUtils.TIME.MILLISECONDS_IN_A_SECOND);
  const cookie = `${name}=${value}; SameSite=Strict; Domain=${cookieDomain}; Path=/; Expires=${expirationDate.toUTCString()}`; // eslint-disable-next-line functional/immutable-data,unicorn/no-document-cookie

  document.cookie = cookie;
};

const removeCookie = (document, cookieDomain) => name => {
  const expirationDate = genericUtils.DATE.DEFAULT_START_DATE;
  const cookie = `${name}=; SameSite=Strict; Path=/; Domain=${cookieDomain}; Expires=${expirationDate.toUTCString()}`; // eslint-disable-next-line functional/immutable-data,unicorn/no-document-cookie

  document.cookie = cookie;
};

const getCookie = document => name => {
  var _a;

  const cookiesString = decodeURIComponent(document.cookie);
  const cookies = cookiesString.split('; ').reduce((existingCookies, cookieString) => {
    const [key, value] = cookieString.split('=');
    return Object.assign(Object.assign({}, existingCookies), {
      [key]: value
    });
  }, {});
  return (_a = cookies[name]) !== null && _a !== void 0 ? _a : null;
};

const refreshAccessToken = (authManagementExpressUrl, removeCookie, getCookie, setCookie) => async () => {
  removeCookie('tolk_access_token');
  const refreshToken = getCookie('tolk_refresh_token');
  const newAccessToken = (await axios.post(`${authManagementExpressUrl}/v1/token`, {
    grant_type: 'refresh_token',
    refresh_token: refreshToken
  })).data.access_token;
  setCookie('tolk_access_token', newAccessToken);
};

const isAccessTokenExpired = () => {
  const accessToken = getCookie(document)('tolk_access_token');

  if (accessToken === null) {
    return null;
  }

  const decodedAccessToken = jwtDecode(accessToken);

  if (!genericUtils.isObject(decodedAccessToken)) {
    return null;
  }

  if (typeof decodedAccessToken.exp !== 'number') {
    return null;
  }

  if (decodedAccessToken.exp * genericUtils.TIME.MILLISECONDS_IN_A_SECOND > Date.now()) {
    return false;
  }

  return true;
};

const authenticatedRequest = (axios, authManagementExpressUrl, removeCookie, getCookie, setCookie) => async (method, url, body = {}, headers = {}) => {
  const accessToken = getCookie('tolk_access_token');
  const requestHeaders = accessToken === null ? Object.assign({}, headers) : Object.assign(Object.assign({}, headers), {
    Authorization: `Bearer ${accessToken}`
  });

  try {
    switch (method) {
      case 'get':
      case 'delete':
        return (await axios[method](url, {
          headers: Object.assign({}, requestHeaders)
        })).data;

      case 'post':
      case 'put':
      case 'patch':
        return (await axios[method](url, body, {
          headers: Object.assign({}, requestHeaders)
        })).data;

      default:
        throw new Error('HTTP method not allowed');
    }
  } catch (error) {
    if (isAxiosRestError(error) && error.response.status === genericUtils.HTTP_CODE.UNAUTHORIZED) {
      await refreshAccessToken(authManagementExpressUrl, removeCookie, getCookie, setCookie)();
      const newAccessToken = getCookie('tolk_access_token');
      const newRequestHeaders = newAccessToken === null ? Object.assign({}, headers) : Object.assign(Object.assign({}, headers), {
        Authorization: `Bearer ${newAccessToken}`
      });
      return (await axios[method](url, {
        headers: Object.assign({}, newRequestHeaders)
      })).data;
    }

    throw error;
  }
};

const isAuthenticated = () => {
  const accessToken = getCookie(document)('tolk_access_token');

  if (accessToken === null) {
    return false;
  }

  const decodedAccessToken = jwtDecode(accessToken);

  if (!genericUtils.isObject(decodedAccessToken)) {
    return false;
  }

  if (typeof decodedAccessToken.exp !== 'number') {
    return false;
  }

  if (decodedAccessToken.exp * genericUtils.TIME.MILLISECONDS_IN_A_SECOND < Date.now()) {
    return false;
  }

  return true;
};

const getAuthenticatedUserId = () => {
  const accessToken = getCookie(document)('tolk_access_token');
  const decodedAccessToken = jwtDecode(accessToken !== null && accessToken !== void 0 ? accessToken : '');

  if (!genericUtils.isObject(decodedAccessToken)) {
    throw new TypeError('Invalid access token');
  }

  if (typeof decodedAccessToken.sub !== 'string') {
    throw new TypeError('Invalid access token');
  }

  return decodedAccessToken.sub;
};

const getAuthenticatedUserEmail = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const decodeIdToken = jwtDecode(idToken !== null && idToken !== void 0 ? idToken : '');

  if (!genericUtils.isObject(decodeIdToken)) {
    throw new TypeError('Invalid id token');
  }

  if (typeof decodeIdToken.email !== 'string') {
    throw new TypeError('Invalid id token');
  }

  return decodeIdToken.email;
};

const getAuthenticatedUserGivenName = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const decodeIdToken = jwtDecode(idToken !== null && idToken !== void 0 ? idToken : '');

  if (!genericUtils.isObject(decodeIdToken)) {
    throw new TypeError('Invalid id token');
  }

  if (typeof decodeIdToken.given_name !== 'string') {
    throw new TypeError('Invalid id token');
  }

  return decodeIdToken.given_name;
};

const getAuthenticatedUserFamilyName = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const decodeIdToken = jwtDecode(idToken !== null && idToken !== void 0 ? idToken : '');

  if (!genericUtils.isObject(decodeIdToken)) {
    throw new TypeError('Invalid id token');
  }

  if (typeof decodeIdToken.family_name !== 'string') {
    throw new TypeError('Invalid id token');
  }

  return decodeIdToken.family_name;
};

const isTolkAdmin = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const decodeIdToken = jwtDecode(idToken !== null && idToken !== void 0 ? idToken : '');

  if (!genericUtils.isObject(decodeIdToken)) {
    throw new TypeError('Invalid id token');
  }

  if (typeof decodeIdToken.scope !== 'string') {
    throw new TypeError('Invalid id token');
  }

  return decodeIdToken.scope.includes('role:tolk-admin');
};

const getGivenNameAndPartialFamilyName = () => {
  const givenName = getAuthenticatedUserGivenName().toLowerCase();
  const familyName = getAuthenticatedUserFamilyName().toLowerCase();
  return `${givenName.replace(/^(?:[0-9A-Z_a-z])/, letter => letter.toUpperCase())} ${familyName.charAt(genericUtils.ARRAY.FIRST_INDEX).toUpperCase()}.`;
};

const logout = (authenticatedRequest, cookieDomain) => () => {
  const refreshToken = getCookie(document)('tolk_refresh_token');

  if (genericUtils.isString(refreshToken)) {
    void authenticatedRequest('post', '/v1/revoke', {
      refresh_token: refreshToken
    });
  }

  removeCookie(document, cookieDomain)('tolk_access_token');
  removeCookie(document, cookieDomain)('tolk_id_token');
  removeCookie(document, cookieDomain)('tolk_refresh_token');
  window.localStorage.removeItem('tolk_authorization_infos');
  window.location.assign('/');
};

export { authenticatedRequest, getAuthenticatedUserEmail, getAuthenticatedUserFamilyName, getAuthenticatedUserGivenName, getAuthenticatedUserId, getCookie, getGivenNameAndPartialFamilyName, isAccessTokenExpired, isAuthenticated, isAxiosError, isAxiosRestError, isRestError, isTolkAdmin, logout, refreshAccessToken, removeCookie, setCookie };
